'use client';

import {
    Enum_Componentgeneraldetail_Viewvariant
} from "@/app/lib/generated/graphql-types";
import ErrorBox from "@/app/__to_be_integrated/_____components/_NEW/ErrorBox";
import { DetailSplitContentWrapperFrame } from "@/app/features/shared/detail-section/ui/shared/DetailSectionFrames";
import { PLACEHOLDER_IMAGE } from "@/app/types/common/constants";
import EditableTypographyV2 from "@/app/features/shared/editable-typography/EditableTypographyV2";
import EditableMarkdownV2 from "@/app/features/shared/editable-markdown/EditableMarkdownV2";
import { positionForDetailsMenuAlignment } from "@/app/features/shared/detail-section/helper/helper";
import MediaView from "@/app/features/shared/media-view/ui/public/MediaView.public";
import { DetailsMenuAlignment } from "@/app/features/shared/detail-section/types/types";
import { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import useMarkdownEditScrollLock from "@/app/features/shared/editable-markdown/hooks/useMarkdownEditScrollLock";
import { alpha, Box, Stack } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import EditMediaButtonOverlay from "@/app/features/shared/EditMediaButtonOverlay";
import { DetailEditable } from "@/app/features/shared/types/types";
import Section from "@/app/features/shared/snap-section/Section";
import Grid from "@mui/material/Grid2";

type DetailSectionEditableData = DetailEditable;
type DetailDocumentEditable = NonNullable<DetailSectionEditableData['documents']>[number];

export interface DetailSectionEditableProps {
    data: DetailSectionEditableData;
    alignment: DetailsMenuAlignment;
    onTitleChange: (title: string) => void;
    onSubtitleChange: (title: string) => void;
    onDescriptionChange: (title: string) => void;
    onImageChange: (image: MediaRef | null) => void;
    textCommitMode?: 'change' | 'blur';
}

export default function DetailSectionEditable(props: DetailSectionEditableProps) {
    switch (props.data.viewVariant) {
        case Enum_Componentgeneraldetail_Viewvariant.SplitContent:
            return <SplitContentVariant {...props} />;
        case Enum_Componentgeneraldetail_Viewvariant.Documents:
            return <DocumentsVariant {...props} />;
        case Enum_Componentgeneraldetail_Viewvariant.DocumentsMinimal:
            return <DocumentsMinimalVariant {...props} />;
        default:
            return <ErrorBox error="NOT YET IMPLEMENTED" />;
    }
}

function SplitContentVariant({ data, alignment, onTitleChange, onSubtitleChange, onDescriptionChange, onImageChange, textCommitMode }: DetailSectionEditableProps) {
    const { isMarkdownEditing, onMarkdownEditingChange, markdownEditingContainerSx } = useMarkdownEditScrollLock();

    return (
        <DetailSplitContentWrapperFrame
            alignment={alignment}
            maxSplitPosition={data.splitMax || data.splitInitial || 50}
            initialSplitPosition={data.splitInitial || 50}
            scrollDisabled={isMarkdownEditing}
            content={
                <>
                        <EditableTypographyV2
                            label="Überschrift"
                            variant="h1"
                            component="h2"
                            color="primary"
                            value={data.title || ''}
                            onChange={onTitleChange}
                            commitMode={textCommitMode}
                            autoSizeDuringEdit={textCommitMode !== 'blur'}
                        />
                        <EditableTypographyV2
                            label="Untertitel"
                            variant="subtitle1"
                            color="text.secondary"
                            value={data.subtitle || ''}
                            onChange={onSubtitleChange}
                            commitMode={textCommitMode}
                            autoSizeDuringEdit={textCommitMode !== 'blur'}
                        />
                        <Box sx={markdownEditingContainerSx}>
                            <EditableMarkdownV2
                                label="Beschreibung"
                                value={data.description || ''}
                                onChange={onDescriptionChange}
                                commitMode={textCommitMode}
                                onEditingChange={onMarkdownEditingChange}
                                typographyOverrides={{
                                    paragraph: 'body2'
                                }}
                            />
                        </Box>
                </>
            }
            background={
                <>
                    <MediaView
                        src={data.image || PLACEHOLDER_IMAGE}
                        magnifier="image"
                        infoViewType="image"
                        detailsMenuPosition={positionForDetailsMenuAlignment(alignment)}
                        showDetailsMenu
                        fill
                    />
                    <EditMediaButtonOverlay
                        align={alignment === 'row' ? 'right' : alignment == 'row-reverse' ? 'left' : 'center'}
                        onImageChange={onImageChange} />
                </>
            }
        />
    )
}

function DocumentsVariant({ data, alignment, onTitleChange, onSubtitleChange, onImageChange, textCommitMode }: DetailSectionEditableProps) {
    const documents = getSortedDocuments(data);

    return (
        <Section className="relative">
            <Box sx={{ display: 'flex', flex: '1 1 auto', alignItems: 'flex-end' }}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                    }}
                >
                    <MediaView
                        src={data.image || PLACEHOLDER_IMAGE}
                        width={1080}
                        height={900}
                        slots={{
                            root: {
                                sx: {
                                    height: '100%',
                                }
                            },
                            image: {
                                style: {
                                    width: '100%',
                                    objectFit: 'cover',
                                    objectPosition: data.imagePosition || 'center',
                                }
                            }
                        }}
                        magnifier="image"
                        detailsMenuPosition={positionForDetailsMenuAlignment(alignment)}
                        showDetailsMenu
                    />
                    <EditMediaButtonOverlay
                        align={alignment === 'row' ? 'right' : alignment === 'row-reverse' ? 'left' : 'center'}
                        onImageChange={onImageChange}
                    />
                </Box>
                <Stack
                    spacing={4}
                    alignItems="center"
                    sx={{
                        display: 'flex',
                        minWidth: '100%',
                        px: '10%',
                        pt: 4,
                        pb: 8,
                        zIndex: 10,
                        borderTop: (theme) => `1px solid ${alpha(theme.palette.primary.main, .5)}`,
                        backgroundColor: (theme) => alpha(theme.palette.background.paper, .75),
                    }}
                >
                    <DocumentsHeadingEditable
                        title={data.title}
                        subtitle={data.subtitle}
                        onTitleChange={onTitleChange}
                        onSubtitleChange={onSubtitleChange}
                        textCommitMode={textCommitMode}
                    />
                    {documents.length > 0 && (
                        <Grid
                            container
                            spacing={2}
                            columns={5}
                            sx={{
                                display: 'flex',
                                flex: '1 1 auto',
                                height: '100%',
                                minWidth: 'inherit',
                            }}
                        >
                            {documents.map((document, index) => (
                                <Grid key={document.id ?? index} size={1}>
                                    <MediaView src={document.media ?? PLACEHOLDER_IMAGE} width={300} height={400} />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Stack>
            </Box>
        </Section>
    )
}

function DocumentsMinimalVariant({ data, onTitleChange, onSubtitleChange, onDescriptionChange, textCommitMode }: DetailSectionEditableProps) {
    const documents = getSortedDocuments(data);
    const { onMarkdownEditingChange, markdownEditingContainerSx } = useMarkdownEditScrollLock();

    return (
        <Section
            className="relative"
            style={{ height: 'calc(100svh - var(--header-height, 138px))' }}
        >
            <Stack
                data-detail-section-variant="documents-minimal"
                spacing={8}
                alignItems="center"
                justifyContent="center"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%',
                    height: 'calc(100svh - var(--header-height, 138px))',
                    maxHeight: 'calc(100svh - var(--header-height, 138px))',
                    minHeight: 0,
                    minWidth: 0,
                    px: '10%',
                    pt: 4,
                    pb: 8,
                    zIndex: 10,
                    overflow: 'hidden',
                }}
            >
                <DocumentsHeadingEditable
                    title={data.title}
                    subtitle={data.subtitle}
                    onTitleChange={onTitleChange}
                    onSubtitleChange={onSubtitleChange}
                    textCommitMode={textCommitMode}
                    sx={{
                        flex: '0 1 auto',
                        maxWidth: '100%',
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                />

                {documents.length > 0 && (
                    <Box
                        data-detail-section-documents
                        sx={{
                            display: 'flex',
                            flex: '1 10 auto',
                            width: '100%',
                            minWidth: 0,
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'grid',
                                height: '100%',
                                width: '100%',
                                minWidth: 0,
                                minHeight: 0,
                                gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
                                gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
                                gap: 2,
                                gridAutoFlow: 'row dense',
                                '& > *': { gridColumn: 'span 2' },
                                '& > *:nth-of-type(5n+4), & > *:nth-of-type(5n+5)': {
                                    gridColumn: 'span 3',
                                },
                            }}
                        >
                            {documents.map((document, index) => (
                                <Box key={document.id ?? index} sx={{ width: '100%', minHeight: 0, overflow: 'hidden' }}>
                                    <MediaView
                                        src={document.media ?? PLACEHOLDER_IMAGE}
                                        fill
                                        objectFit="cover"
                                        slots={{
                                            root: {
                                                sx: {
                                                    height: '100%',
                                                    minHeight: 0,
                                                }
                                            },
                                            inner: {
                                                sx: {
                                                    height: '100%',
                                                    minHeight: 0,
                                                }
                                            },
                                            video: {
                                                autoPlay: true,
                                                loop: true,
                                                style: {
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}
                <Box
                    sx={{
                        flex: '0 1 auto',
                        width: '100%',
                        minHeight: 0,
                        overflow: 'hidden',
                        textAlign: 'center',
                        ...markdownEditingContainerSx
                    }}
                >
                    <EditableMarkdownV2
                        label="Beschreibung"
                        value={data.description || ''}
                        onChange={onDescriptionChange}
                        commitMode={textCommitMode}
                        onEditingChange={onMarkdownEditingChange}
                    />
                </Box>
            </Stack>
        </Section>
    )
}

function DocumentsHeadingEditable({
    title,
    subtitle,
    onTitleChange,
    onSubtitleChange,
    textCommitMode,
    sx,
}: {
    title?: string | null;
    subtitle?: string | null;
    onTitleChange: (title: string) => void;
    onSubtitleChange: (subtitle: string) => void;
    textCommitMode?: 'change' | 'blur';
    sx?: SxProps<Theme>;
}) {
    return (
        <Stack spacing={1} alignItems="center" sx={{ width: '100%', ...sx }}>
            <EditableTypographyV2
                label="Überschrift"
                variant="h2"
                component="h2"
                color="primary"
                value={title || ''}
                onChange={onTitleChange}
                commitMode={textCommitMode}
                autoSizeDuringEdit={textCommitMode !== 'blur'}
            />
            <EditableTypographyV2
                label="Untertitel"
                variant="h5"
                component="h3"
                color="text.secondary"
                value={subtitle || ''}
                onChange={onSubtitleChange}
                commitMode={textCommitMode}
                autoSizeDuringEdit={textCommitMode !== 'blur'}
            />
        </Stack>
    )
}

function getSortedDocuments(data: DetailSectionEditableData): DetailDocumentEditable[] {
    return [...(data.documents ?? [])]
        .sort((d1, d2) => d2.order - d1.order);
}
